import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { apiCall } from "../../lib/api-client.js";
import {
  ApiError,
  type Organization,
} from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { Spinner } from "../../components/Spinner.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";

interface FormState {
  name: string;
  code: string;
  maxEmployees: string;
  expiresAt: string;
}

const EMPTY: FormState = { name: "", code: "", maxEmployees: "", expiresAt: "" };

interface MintedToken {
  org: Organization;
  apiToken: string;
}

/**
 * T1.2.2 · 创建 org 后 server 201 响应里返一次性明文 apiToken。
 * UI 切换到「token 揭示视图」展示 + 复制 + amber 警示（§5 非 bug 不红色），
 * 必须显式「我已复制」才关闭并触发 onCreated。
 */
export function OrganizationCreateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (org: Organization) => void;
}): JSX.Element | null {
  const auth = useAtomValue(authAtom);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [minted, setMinted] = useState<MintedToken | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm(EMPTY);
      setError(null);
      setSubmitting(false);
      setMinted(null);
      setCopied(false);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (submitting || !auth) return;
    setError(null);
    setSubmitting(true);
    const body: Record<string, unknown> = {
      name: form.name.trim(),
      code: form.code.trim(),
    };
    const max = Number.parseInt(form.maxEmployees, 10);
    if (!Number.isNaN(max) && max > 0) body.maxEmployees = max;
    if (form.expiresAt.trim()) body.expiresAt = form.expiresAt.trim();

    try {
      const created = await apiCall<Organization>("POST", "/api/organizations", {
        body,
        token: auth.token,
      });
      // T1.2.2 · 后端返回了一次性明文 token → 切到揭示视图（不立即 close）
      if (created.apiToken) {
        setMinted({ org: created, apiToken: created.apiToken });
        return;
      }
      // Defensive：万一后端没返（理论上不会发生），走老路径
      onCreated(created);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "org-code-already-exists" || err.status === 409) {
          setError(`组织代号 ${form.code} 已存在，请换一个。`);
        } else if (err.status === 400) {
          setError("请填写必填项（名称 / 代号）。");
        } else {
          setError(err.message || `创建失败（${err.code}）`);
        }
      } else {
        setError("网络异常，请稍后再试");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (): Promise<void> => {
    if (!minted) return;
    try {
      await navigator.clipboard.writeText(minted.apiToken);
      setCopied(true);
    } catch {
      // clipboard 权限拒绝时回退 select-all（用户可手动 Cmd+C）
      setCopied(false);
    }
  };

  const handleAcknowledge = (): void => {
    if (!minted) return;
    onCreated(minted.org);
    onClose();
  };

  if (minted) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="minted-token-title"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      >
        <div className="w-full max-w-md space-y-4 rounded-lg border border-dosia-border bg-dosia-bg-light p-6 shadow-lg">
          <div className="space-y-1">
            <h2
              id="minted-token-title"
              className="text-base font-semibold text-dosia-text"
            >
              {minted.org.name} 创建成功
            </h2>
            <p className="text-xs text-dosia-text-secondary">
              代号：<code className="font-mono">{minted.org.code}</code>
            </p>
          </div>

          {/* amber 警示 — CLAUDE.md §5 非 bug 不红色 */}
          <div className="rounded border border-amber-500/30 bg-amber-500/15 px-3 py-2 text-xs text-amber-500">
            此 API token 仅显示一次，关闭后无法找回。请立即复制并妥善保存（password
            manager / 内部 secret store）。
          </div>

          <div className="space-y-1">
            <span className="text-sm text-dosia-text-secondary">API token</span>
            <code
              data-testid="minted-api-token"
              className="block w-full select-all break-all rounded border border-dosia-border bg-dosia-bg-input px-3 py-2 font-mono text-xs text-dosia-text"
            >
              {minted.apiToken}
            </code>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => void handleCopy()}
              className="rounded border border-dosia-border px-3 py-1.5 text-sm text-dosia-text hover:bg-dosia-bg-lighter"
            >
              {copied ? "已复制 ✓" : "复制"}
            </button>
            <button
              type="button"
              onClick={handleAcknowledge}
              className="rounded bg-dosia-accent px-3 py-1.5 text-sm font-medium text-dosia-bg-light hover:bg-dosia-accent-hover"
            >
              我已复制，关闭
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-org-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md space-y-4 rounded-lg border border-dosia-border bg-dosia-bg-light p-6 shadow-lg"
      >
        <div className="space-y-1">
          <h2 id="create-org-title" className="text-base font-semibold text-dosia-text">
            创建组织
          </h2>
          <p className="text-xs text-dosia-text-secondary">
            组织代号（code）创建后不可改。创建成功会弹出 API token（仅一次）。
          </p>
        </div>

        {error ? <ErrorBanner>{error}</ErrorBanner> : null}

        <label className="block space-y-1">
          <span className="text-sm text-dosia-text-secondary">名称 *</span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            maxLength={64}
            className="w-full rounded border border-dosia-border bg-dosia-bg-input px-3 py-1.5 text-sm text-dosia-text focus:border-dosia-accent focus:outline-none"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-sm text-dosia-text-secondary">
            代号（code，小写英文/数字/连字符）*
          </span>
          <input
            type="text"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            pattern="^[a-z0-9-]+$"
            required
            maxLength={32}
            placeholder="catalyst"
            className="w-full rounded border border-dosia-border bg-dosia-bg-input px-3 py-1.5 text-sm text-dosia-text focus:border-dosia-accent focus:outline-none"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1">
            <span className="text-sm text-dosia-text-secondary">员工上限</span>
            <input
              type="number"
              min={1}
              value={form.maxEmployees}
              onChange={(e) =>
                setForm((f) => ({ ...f, maxEmployees: e.target.value }))
              }
              placeholder="100"
              className="w-full rounded border border-dosia-border bg-dosia-bg-input px-3 py-1.5 text-sm text-dosia-text focus:border-dosia-accent focus:outline-none"
            />
          </label>

          <label className="block space-y-1">
            <span className="text-sm text-dosia-text-secondary">到期日（可选）</span>
            <input
              type="date"
              value={form.expiresAt}
              onChange={(e) =>
                setForm((f) => ({ ...f, expiresAt: e.target.value }))
              }
              className="w-full rounded border border-dosia-border bg-dosia-bg-input px-3 py-1.5 text-sm text-dosia-text focus:border-dosia-accent focus:outline-none"
            />
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded border border-dosia-border px-3 py-1.5 text-sm text-dosia-text-secondary hover:bg-dosia-bg-lighter disabled:opacity-60"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded bg-dosia-accent px-3 py-1.5 text-sm font-medium text-dosia-bg-light hover:bg-dosia-accent-hover disabled:opacity-60"
          >
            {submitting ? <Spinner size="sm" className="text-dosia-bg-light" /> : null}
            创建
          </button>
        </div>
      </form>
    </div>
  );
}
